import type {
  Granularity,
  PeopleCountsResponse,
  PutPeopleCountResponse,
  RangeResponse,
  StatusResponse,
} from './types.js';

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  // Override fetch for tests. Defaults to global fetch.
  fetch?: typeof fetch;
  // Base path for API calls. Defaults to '' (relative paths under same origin).
  // Tests can pass '' or a fake host. Production uses CloudFront same-origin /api/*.
  baseUrl?: string;
}

export function makeApiClient(getPassword: () => string | null, options: ApiClientOptions = {}) {
  const fetchImpl = options.fetch ?? fetch;
  const baseUrl = options.baseUrl ?? '';

  async function request<T>(path: string, accept = 'application/json'): Promise<T> {
    const password = getPassword();
    if (password === null) {
      throw new UnauthorizedError();
    }
    const response = await fetchImpl(`${baseUrl}${path}`, {
      method: 'GET',
      headers: { accept, 'x-app-password': password },
    });
    if (response.status === 401 || response.status === 403) {
      throw new UnauthorizedError();
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new ApiError(response.status, text || `HTTP ${response.status}`);
    }
    if (accept === 'application/json') {
      return (await response.json()) as T;
    }
    return (await response.text()) as unknown as T;
  }

  return {
    getStatus(): Promise<StatusResponse> {
      return request<StatusResponse>('/api/status');
    },

    getRange(fromIso: string, toIso: string, granularity: Granularity): Promise<RangeResponse> {
      const params = new URLSearchParams({ from: fromIso, to: toIso, granularity });
      return request<RangeResponse>(`/api/range?${params.toString()}`);
    },

    getPeopleCounts(fromYyyymmdd: string, toYyyymmdd: string): Promise<PeopleCountsResponse> {
      const params = new URLSearchParams({ from: fromYyyymmdd, to: toYyyymmdd });
      return request<PeopleCountsResponse>(`/api/people-count?${params.toString()}`);
    },

    async putPeopleCount(yyyymmdd: string, peopleCount: number): Promise<PutPeopleCountResponse> {
      const password = getPassword();
      if (password === null) throw new UnauthorizedError();
      const params = new URLSearchParams({ date: yyyymmdd });
      const response = await fetchImpl(`${baseUrl}/api/people-count?${params.toString()}`, {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
          'x-app-password': password,
        },
        body: JSON.stringify({ peopleCount }),
      });
      if (response.status === 401 || response.status === 403) {
        throw new UnauthorizedError();
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new ApiError(response.status, text || `HTTP ${response.status}`);
      }
      return (await response.json()) as PutPeopleCountResponse;
    },

    async downloadExport(fromIso: string, toIso: string): Promise<{ filename: string; blob: Blob }> {
      const password = getPassword();
      if (password === null) throw new UnauthorizedError();
      const params = new URLSearchParams({ from: fromIso, to: toIso });
      const response = await fetchImpl(`${baseUrl}/api/export?${params.toString()}`, {
        method: 'GET',
        headers: { accept: 'text/csv', 'x-app-password': password },
      });
      if (response.status === 401 || response.status === 403) {
        throw new UnauthorizedError();
      }
      if (!response.ok) {
        throw new ApiError(response.status, `HTTP ${response.status}`);
      }
      const filename = parseFilename(response.headers.get('content-disposition'));
      const blob = await response.blob();
      return { filename, blob };
    },
  };
}

export type ApiClient = ReturnType<typeof makeApiClient>;

function parseFilename(disposition: string | null): string {
  if (disposition === null) return 'betz-water-export.csv';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return match?.[1] ?? 'betz-water-export.csv';
}
